import type { ShopperSearch } from '@/scapi';
import type { FilterValue } from '@/components/eataly-plp-filters/lib/types';

type ProductSearchRefinement = ShopperSearch.schemas['ProductSearchRefinement'];

export const CGID_REFINEMENT_ATTRIBUTE_ID = 'cgid';

export type CategorySiblingFilterOptions = {
    /** Route category id. Siblings are resolved against this id, not the selected refine. */
    categoryId?: string;
    /** Category custom attribute `c_showCategoryFilter`. */
    showCategoryFilter?: boolean;
};

function findSiblingGroup(values: FilterValue[] | undefined, categoryId: string): FilterValue[] | null {
    if (!values?.length) {
        return null;
    }

    if (values.some((value) => value.value === categoryId)) {
        return values;
    }

    for (const value of values) {
        const nested = findSiblingGroup(value.values, categoryId);
        if (nested) {
            return nested;
        }
    }

    return null;
}

/** Other categories that share the current category's parent, excluding the category itself. */
export function categorySiblingValues(values: FilterValue[] | undefined, categoryId: string): FilterValue[] {
    const siblings = findSiblingGroup(values, categoryId);
    if (!siblings) {
        return [];
    }

    return siblings.filter((value) => value.value !== categoryId && value.hitCount > 0);
}

export function withCategorySiblingFilter(
    refinement: ProductSearchRefinement,
    options: CategorySiblingFilterOptions
): ProductSearchRefinement | null {
    if (refinement.attributeId !== CGID_REFINEMENT_ATTRIBUTE_ID) {
        return refinement;
    }

    if (!options.showCategoryFilter || !options.categoryId) {
        return null;
    }

    const values = categorySiblingValues(refinement.values, options.categoryId);
    if (values.length === 0) {
        return null;
    }

    return {
        ...refinement,
        values,
    };
}
